import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import { EmptyState, PageSkeleton, TableSkeleton } from '../components';
import { PageHeader } from '../components/layout';

const DashboardPage = ({ canReadJiraSettings = false }) => {
  const navigate = useNavigate();
  const [jiraExpiryStatus, setJiraExpiryStatus] = useState(null);
  const [summary, setSummary] = useState({
    totalLogs: 0,
    recentLogs: [],
    failedLogs: 0,
    enabledGroups: 0,
    activeFields: 0,
    activePersons: 0,
    activeTasks: 0,
    pendingLogs: 0
  });
  const [trend, setTrend] = useState([]);
  const [trendError, setTrendError] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        const [
          logsResult,
          failedLogsResult,
          groupsResult,
          fieldsResult,
          personsResult,
          tasksResult,
          trendResult
        ] = await Promise.allSettled([
          api.getLogs({ page: 1, page_size: 5 }),
          api.getLogs({ page: 1, page_size: 1, status: 'failed' }),
          api.getGroups(),
          api.getFields(),
          api.getPersons(),
          api.getQueryTriggerTasks(),
          api.getLogTrend()
        ]);

        const dataOf = (result, fallback) => result.status === 'fulfilled' ? result.value.data : fallback;
        const logsData = dataOf(logsResult, {});
        const failedLogsData = dataOf(failedLogsResult, {});
        const groupsData = dataOf(groupsResult, []);
        const fieldsData = dataOf(fieldsResult, []);
        const personsData = dataOf(personsResult, []);
        const tasksData = dataOf(tasksResult, []);
        const groups = Array.isArray(groupsData) ? groupsData : [];
        const fields = Array.isArray(fieldsData) ? fieldsData : fieldsData.fields || [];
        const persons = Array.isArray(personsData) ? personsData : personsData.persons || [];
        const tasks = Array.isArray(tasksData) ? tasksData : [];

        const trendData = dataOf(trendResult, null);
        setTrendError(!Array.isArray(trendData?.days));
        setTrend(Array.isArray(trendData?.days) ? trendData.days.map(day => ({
          ...day,
          key: day.date,
          label: day.date.slice(5).split('-').map(Number).join('/')
        })) : []);

        setSummary({
          totalLogs: logsData.total || 0,
          recentLogs: Array.isArray(logsData.logs) ? logsData.logs : [],
          failedLogs: failedLogsData.total || 0,
          enabledGroups: groups.filter(group => group.is_active !== false).length,
          activeFields: fields.filter(field => field.is_active !== false).length,
          activePersons: persons.filter(person => person.is_active !== false).length,
          activeTasks: tasks.filter(task => task.is_active !== false).length,
          pendingLogs: logsData.logs?.filter(log => ['pending', 'delayed'].includes(log.status)).length || 0
        });
      } catch (err) {
        console.error('获取 Dashboard 数据失败:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  useEffect(() => {
    if (!canReadJiraSettings) {
      setJiraExpiryStatus(null);
      return;
    }
    api.getSystemJiraConfig()
      .then((response) => setJiraExpiryStatus(response.data?.expiry_status || null))
      .catch(() => setJiraExpiryStatus(null));
  }, [canReadJiraSettings]);

  const getStatusBadge = (status) => {
    const statusMap = {
      success: { class: 'badge-success', label: '成功' },
      pending: { class: 'badge-warning', label: '待处理' },
      failed: { class: 'badge-danger', label: '失败' }
    };
    const config = statusMap[status] || { class: 'badge-default', label: status || '-' };
    return <span className={`badge ${config.class}`}>{config.label}</span>;
  };

  const metricItems = [
    { label: '操作记录总数', value: summary.totalLogs },
    { label: '失败记录', value: summary.failedLogs, tone: summary.failedLogs ? 'danger' : '' },
    { label: '近期待处理', value: summary.pendingLogs, tone: summary.pendingLogs ? 'warning' : '' },
    { label: '启用规则组', value: summary.enabledGroups },
    { label: '启用字段', value: summary.activeFields },
    { label: '定时任务', value: summary.activeTasks }
  ];

  const trendMax = Math.max(1, ...trend.map(day => day.total));
  const attentionLogs = summary.recentLogs.filter(log => ['failed', 'pending', 'delayed'].includes(log.status)).slice(0, 4);

  return (
    <div className="dashboard-console-page">
      <PageHeader
        title="自动化概览"
      />

      {canReadJiraSettings && jiraExpiryStatus && jiraExpiryStatus.level !== 'healthy' && (
        <div className={`system-config-expiry is-${jiraExpiryStatus.level === 'expired' ? 'error' : jiraExpiryStatus.level}`}>
          <div>
            <strong>系统 Jira Token</strong>
            <span>{jiraExpiryStatus.message}</span>
          </div>
          <button className="btn btn-secondary btn-sm" onClick={() => navigate('/jira-settings')}>
            查看配置
          </button>
        </div>
      )}

      {loading ? (
        <div className="dashboard-overview-skeleton">
          <PageSkeleton metricCount={6} rowCount={0} label="加载概览中" />
        </div>
      ) : (
        <section className="dashboard-summary-grid" aria-label="运行概览">
          {metricItems.map(metric => (
            <div className={`metric-card dashboard-metric-card ${metric.tone ? `is-${metric.tone}` : ''}`} key={metric.label}>
              <div className="metric-label">{metric.label}</div>
              <div className="metric-value">{metric.value}</div>
            </div>
          ))}
        </section>
      )}

      <div className="dashboard-workspace-grid">
        <section className="dashboard-surface dashboard-trend-surface">
          <header className="dashboard-surface-header">
            <div>
              <h2>执行趋势</h2>
              <p>最近 7 天的全部自动化记录（北京时间）。</p>
            </div>
          </header>
          <div className="dashboard-surface-body">
          {loading ? <TableSkeleton rowCount={5} columnCount={7} label="加载执行趋势中" /> : trendError ? (
            <p role="alert">执行趋势加载失败，请刷新页面重试。</p>
          ) : <div className="dashboard-execution-trend" aria-label="最近七天执行趋势">
            {trend.map(day => (
              <div className="dashboard-trend-day" key={day.key}>
                <div className="dashboard-trend-bar-track">
                  <span
                    className="dashboard-trend-bar"
                    style={{ height: `${Math.max(day.total ? 18 : 4, (day.total / trendMax) * 100)}%` }}
                    title={`${day.label}: ${day.total} 条`}
                  />
                  {day.failed > 0 && <span className="dashboard-trend-failure" title={`${day.failed} 条失败`} />}
                </div>
                <strong>{day.total}</strong>
                <span>{day.label}</span>
              </div>
            ))}
          </div>}
          </div>
        </section>

        <section className="dashboard-surface dashboard-attention-surface">
          <header className="dashboard-surface-header">
            <div>
              <h2>关注队列</h2>
              <p>失败、待处理和延迟记录优先处理。</p>
            </div>
          </header>
          <div className="dashboard-surface-body">
          <div className="dashboard-attention-queue">
            {attentionLogs.length > 0 ? attentionLogs.map(log => (
              <button className="dashboard-attention-item" key={log.id} onClick={() => navigate('/logs')}>
                <span>
                  <strong>{log.issue_key || '未关联工单'}</strong>
                  <small>{log.action || '-'}</small>
                </span>
                {getStatusBadge(log.status)}
              </button>
            )) : (
              <EmptyState title={summary.failedLogs ? `${summary.failedLogs} 条失败记录待排查` : '当前没有待关注记录'} description={summary.failedLogs ? '前往操作记录查看失败原因。' : '新的失败或待处理任务会显示在这里。'} />
            )}
          </div>
          </div>
        </section>
      </div>

      <div className="dashboard-workspace-grid dashboard-workspace-grid-secondary">
        <section className="dashboard-surface dashboard-recent-surface">
          <header className="dashboard-surface-header">
            <div>
              <h2>最近操作记录</h2>
              <p>展示最近 5 条自动化处理结果。</p>
            </div>
            <button className="btn btn-secondary btn-sm" onClick={() => navigate('/logs')}>查看全部</button>
          </header>
          <div className="dashboard-surface-body dashboard-recent-surface-body">
          {loading ? (
            <TableSkeleton rowCount={4} columnCount={4} label="加载近期操作中" />
          ) : summary.recentLogs.length === 0 ? (
            <EmptyState title="暂无操作记录" description="新的处理结果会显示在这里。" />
          ) : (
            <div className="dashboard-recent-operations">
              <div className="table-container dashboard-recent-table-wrap" style={{ border: 'none', borderRadius: 0 }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>工单 Key</th>
                      <th>操作</th>
                      <th style={{ width: '100px' }}>状态</th>
                      <th style={{ width: '150px' }}>时间</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.recentLogs.map(log => (
                      <tr key={log.id}>
                        <td>{log.issue_key || '-'}</td>
                        <td>{log.action || '-'}</td>
                        <td>{getStatusBadge(log.status)}</td>
                        <td style={{ color: 'var(--text-tertiary)', fontSize: '13px' }}>{log.created_at || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="dashboard-recent-mobile-list responsive-operation-list">
                {summary.recentLogs.map(log => (
                  <article className="responsive-operation-row" key={log.id}>
                    <div className="responsive-operation-main">
                      <strong>{log.issue_key || '-'}</strong>
                      <span>{log.action || '-'}</span>
                    </div>
                    {getStatusBadge(log.status)}
                    <div className="responsive-operation-meta">
                      <span>执行时间</span>
                      <time>{log.created_at || '-'}</time>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          )}
          </div>
        </section>

        <section className="dashboard-surface dashboard-configuration-surface">
          <header className="dashboard-surface-header">
            <div>
              <h2>配置概览</h2>
              <p>当前可被自动化使用的基础配置。</p>
            </div>
          </header>
          <div className="dashboard-surface-body dashboard-configuration-list">
            <div>
              <span style={{ color: 'var(--text-secondary)' }}>启用字段</span>
              <strong>{loading ? <span className="skeleton-cell" aria-label="加载启用字段数中" /> : summary.activeFields}</strong>
            </div>
            <div>
              <span style={{ color: 'var(--text-secondary)' }}>启用人员</span>
              <strong>{loading ? <span className="skeleton-cell" aria-label="加载启用人员数中" /> : summary.activePersons}</strong>
            </div>
            <div>
              <span style={{ color: 'var(--text-secondary)' }}>启用规则组</span>
              <strong>{loading ? <span className="skeleton-cell" aria-label="加载启用规则组数中" /> : summary.enabledGroups}</strong>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default DashboardPage;
